import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';

import { get as userGet } from 'js/lib/user';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import DegreeLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/DegreeLearnerOnConsumerDesktopMainNav';
import EnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/EnterpriseLearnerOnConsumerDesktopMainNav';
import LoggedInConsumerNoSubNavDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedInConsumerNoSubNavDesktopMainNav';
import LoggedOutConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/LoggedOutConsumerDesktopMainNav';
import OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/desktop/OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav';
import DegreeLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/DegreeLearnerOnConsumerMobileMainNav';
import EnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/EnterpriseLearnerOnConsumerMobileMainNav';
import LoggedInConsumerWithMetaNavLinksMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedInConsumerWithMetaNavLinksMobileMainNav';
import LoggedOutConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/LoggedOutConsumerMobileMainNav';
import OrgScopedEnterpriseLearnerOnConsumerMobileMainNav from 'bundles/composable-page-header-consumer/main-nav-modules/mobile/OrgScopedEnterpriseLearnerOnConsumerMobileMainNav';
import DefaultConsumerPageHeaderProvider from 'bundles/composable-page-header-consumer/providers/DefaultConsumerPageHeaderProvider';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import HeaderContextSwitcher from 'bundles/composable-page-header/components/helpers/HeaderContextSwitcher';
import DefaultMetaNavigation from 'bundles/composable-page-header/components/meta-nav/DefaultMetaNavigation';
import { DEFAULT_META_NAV_HEIGHT } from 'bundles/composable-page-header/constants';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  // A list of banner components that this page header may display. Name is used for key.
  banners?: Array<{
    name: string;
    component: React.ReactNode;
  }>;
};

const SearchPageHeader = ({ banners }: Props) => {
  const { chosenEnterpriseProgram, chosenDegree } = useConsumerMyPageHeader();
  const user = userGet();
  const isOrgScoped = useIsOrgScopedHome();

  const enterpriseHeader = isOrgScoped ? (
    <OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav />
  ) : (
    <EnterpriseLearnerOnConsumerDesktopMainNav />
  );
  const enterpriseMobileHeader = isOrgScoped ? (
    <OrgScopedEnterpriseLearnerOnConsumerMobileMainNav showSearch={false} />
  ) : (
    <EnterpriseLearnerOnConsumerMobileMainNav showSearch={false} />
  );
  const desktopNav = (
    <HeaderContextSwitcher
      degreeHeader={<DegreeLearnerOnConsumerDesktopMainNav />}
      enterpriseHeader={enterpriseHeader}
      consumerHeader={<LoggedInConsumerNoSubNavDesktopMainNav />}
      loggedOutHeader={<LoggedOutConsumerDesktopMainNav />}
      userId={user?.id}
      showDegree={!!chosenDegree}
      showEntProgram={!!chosenEnterpriseProgram}
    />
  );

  const mobileNav = (
    <HeaderContextSwitcher
      degreeHeader={<DegreeLearnerOnConsumerMobileMainNav />}
      enterpriseHeader={enterpriseMobileHeader}
      consumerHeader={<LoggedInConsumerWithMetaNavLinksMobileMainNav showSearch={false} />}
      loggedOutHeader={<LoggedOutConsumerMobileMainNav showSearch={false} />}
      userId={user?.id}
      showDegree={!!chosenDegree}
      showEntProgram={!!chosenEnterpriseProgram}
    />
  );

  return (
    <GlobalPageHeaderContainer
      desktopMainNav={desktopNav}
      mobileMainNav={mobileNav}
      metaNav={<DefaultMetaNavigation />}
      scrollMovementOffset={{
        desktop: DEFAULT_META_NAV_HEIGHT,
        mobile: 0,
      }}
      isPositionStatic={false}
      banners={banners}
    />
  );
};

/**
 * This is the default header using the Composable Header pattern. This header is best for when
 * we want learners to easily navigate around the Coursera platform.
 *
 * Note: Do not make direct changes to this header unless explicitly necessary! If you need  more functionality
 * beyond what this header provides, it is encouraged to make a separate header renderer instead.
 */
const SearchPageHeaderWithProvider = ({ banners }: Props) => {
  const addedHeaderHeight = DEFAULT_META_NAV_HEIGHT;
  return (
    <StatsigProvider>
      <GlobalPageHeaderStateProvider additionalDefaultPageHeaderHeight={addedHeaderHeight}>
        <DefaultConsumerPageHeaderProvider>
          <SearchPageHeader banners={banners} />
        </DefaultConsumerPageHeaderProvider>
      </GlobalPageHeaderStateProvider>
    </StatsigProvider>
  );
};

export default SearchPageHeaderWithProvider;
